import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, DataGrid } from '@ibsys/kit';
import type { DataGridLeafColumn } from '@ibsys/kit';
import { ConsultantTrendItem, TrendDirection } from '@/entities/dashboards';
import { cn } from '@/shared/lib';
import { EmptyState } from '@/shared/ui';
import { dashboardGridHeight, fmtSigned, trendArrow, trendBadgeClass } from './utils';

type Props = {
  items: ConsultantTrendItem[];
};

/** Порядок направлений в сводке-счётчиках: рост → ровно → падение. */
const DIRECTION_ORDER: TrendDirection[] = ['up', 'flat', 'down'];

/**
 * Карточка «Тренд по консультантам»: таблица величины недельного изменения
 * общего балла с бейджем направления (↑/→/↓) и сводка-счётчики по направлениям.
 * Порядок строк и пороги направления — с бэкенда; клиент только отображает.
 */
export const ConsultantTrendsCard: FC<Props> = ({ items }) => {
  const { t } = useTranslation('qualityDashboard');

  const directionLabel = useMemo(
    (): Record<TrendDirection, string> => ({
      up: t('trends.directions.up'),
      flat: t('trends.directions.flat'),
      down: t('trends.directions.down'),
    }),
    [t],
  );

  const counts = useMemo(() => {
    const acc: Record<TrendDirection, number> = { up: 0, flat: 0, down: 0 };
    for (const item of items) acc[item.direction] += 1;
    return acc;
  }, [items]);

  const columns = useMemo(
    (): DataGridLeafColumn<ConsultantTrendItem>[] => [
      {
        id: 'consultant',
        header: t('trends.columns.consultant'),
        accessor: 'name',
        size: 200,
      },
      {
        id: 'change',
        header: t('trends.columns.change'),
        accessor: 'change',
        size: 120,
        align: 'center',
        className: 'font-bold',
        cell: ({ value }) => fmtSigned(value as number, 1),
      },
      {
        id: 'direction',
        header: t('trends.columns.direction'),
        accessor: 'direction',
        size: 150,
        align: 'center',
        cell: ({ row }) => (
          <span
            className={cn(
              'inline-block rounded-full px-2 py-0.5 text-xs font-bold',
              trendBadgeClass[row.direction],
            )}
          >
            {trendArrow[row.direction]} {directionLabel[row.direction]}
          </span>
        ),
      },
    ],
    [t, directionLabel],
  );

  const tableHeight = dashboardGridHeight(items.length);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('trends.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              {DIRECTION_ORDER.map((direction) => (
                <span
                  key={direction}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-bold',
                    trendBadgeClass[direction],
                  )}
                >
                  {trendArrow[direction]} {directionLabel[direction]}: {counts[direction]}
                </span>
              ))}
            </div>
            <DataGrid
              columns={columns}
              dataSource={{ mode: 'client', data: items }}
              getRowId={(r) => r.name}
              enableColumnResizing
              height={tableHeight}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
